import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ClipboardList, User, Calendar, Edit, Trash2, Plus, CheckCircle, Mail, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const { t } = useLanguage();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = { limit: 200 };
        if (entityFilter !== 'all') {
          params.entity_type = entityFilter;
        }
        const response = await axios.get(`${API}/audit-logs`, { params });
        setLogs(response.data);
      } catch (error) {
        console.error('Error fetching logs:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [entityFilter, t]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return <Plus className="w-4 h-4 text-emerald-400" />;
      case 'UPDATE': return <Edit className="w-4 h-4 text-blue-400" />;
      case 'DELETE': return <Trash2 className="w-4 h-4 text-red-400" />;
      case 'FINALIZE': return <CheckCircle className="w-4 h-4 text-purple-400" />;
      case 'EMAIL': return <Mail className="w-4 h-4 text-cyan-400" />;
      case 'UPLOAD': return <Upload className="w-4 h-4 text-amber-400" />;
      default: return <Edit className="w-4 h-4 text-gray-400" />;
    }
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE': return 'badge-success';
      case 'UPDATE': return 'badge-info';
      case 'DELETE': return 'badge-error';
      case 'FINALIZE': return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      case 'EMAIL': return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
      case 'UPLOAD': return 'badge-warning';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format UUID to be shorter and more readable
  const formatEntityId = (id) => {
    if (!id) return '-';
    return id.length > 8 ? `${id.substring(0, 8)}...` : id;
  };

  // Format details object into readable text
  const formatDetails = (details) => {
    if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
      return null;
    }
    
    const formatValue = (key, value) => {
      // Handle specific known keys with German labels
      const keyLabels = {
        name: 'Name',
        first_name: 'Vorname',
        last_name: 'Nachname',
        employee_id: 'Mitarbeiter-ID',
        period: 'Zeitraum',
        amount: 'Betrag',
        net_salary: 'Netto-Lohn',
        status: 'Status',
        filename: 'Dateiname',
        email: 'E-Mail'
      };
      
      const label = keyLabels[key] || key;
      
      // Format values
      if (typeof value === 'number') {
        return `${label}: ${value.toLocaleString('de-DE')}`;
      }
      if (typeof value === 'boolean') {
        return `${label}: ${value ? 'Ja' : 'Nein'}`;
      }
      return `${label}: ${value}`;
    };
    
    return Object.entries(details)
      .map(([key, value]) => formatValue(key, value))
      .join(' • ');
  };

  // Get action label in German
  const getActionLabel = (action) => {
    const labels = {
      'CREATE': 'Erstellt',
      'UPDATE': 'Aktualisiert',
      'DELETE': 'Gelöscht',
      'FINALIZE': 'Finalisiert',
      'EMAIL': 'E-Mail',
      'UPLOAD': 'Hochgeladen',
      'UPDATE_NET_SALARY': 'Netto aktualisiert',
      'UPLOAD_DOCUMENT': 'Dokument hochgeladen',
      'DELETE_DOCUMENT': 'Dokument gelöscht'
    };
    return labels[action] || action;
  };

  // Get entity type label in German
  const getEntityTypeLabel = (entityType) => {
    const labels = {
      'employee': 'Mitarbeiter',
      'payroll': 'Lohnabrechnung',
      'advance': 'Vorschuss',
      'loan': 'Kredit',
      'user': 'Benutzer',
      'document': 'Dokument',
      'recurring_item': 'Wiederkehrend'
    };
    return labels[entityType] || entityType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-[#E31E24]" />
          <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('auditLog')}</h1>
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-white border-gray-200 text-[#1A1A1A]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="employee">{t('employees')}</SelectItem>
            <SelectItem value="payroll">{t('payroll')}</SelectItem>
            <SelectItem value="advance">{t('advances')}</SelectItem>
            <SelectItem value="loan">{t('loans')}</SelectItem>
            <SelectItem value="user">{t('users')}</SelectItem>
            <SelectItem value="document">{t('documents')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">{t('noData')}</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`log-item-${log.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-gray-100">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge className={getActionBadgeClass(log.action)}>
                            {getActionLabel(log.action)}
                          </Badge>
                          <Badge className="bg-gray-200 text-gray-700 border-0">
                            {getEntityTypeLabel(log.entity_type)}
                          </Badge>
                        </div>
                        
                        {/* Formatted Details - Primary Info */}
                        {formatDetails(log.details) && (
                          <p className="text-[#1A1A1A] mt-2 text-sm font-medium">
                            {formatDetails(log.details)}
                          </p>
                        )}
                        
                        {/* Entity ID - Secondary Info */}
                        <p className="text-gray-400 mt-1 text-xs">
                          <span>ID: </span>
                          <span className="font-mono" title={log.entity_id}>{formatEntityId(log.entity_id)}</span>
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatTimestamp(log.created_at || log.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
